import { useCallback } from "react";
import { useDialogState } from "../../../../hooks/useDialogState";
import type { DocumentDetailDialogTarget } from "../../Library/Documents/DocumentDetailDialog";
import type { BatchJobItemRow } from "./batch_job_item_rows";

export interface UseDocumentDetailTargetResult<Result> {
  /** The document whose details are open, or null. */
  target: DocumentDetailDialogTarget | null;
  open: (row: BatchJobItemRow<Result>) => void;
  close: () => void;
}

/** Which document's details a list is showing. `toTarget` says which document a row stands for —
 *  null for a row with none, which then opens nothing. Must be a stable function. */
export function useDocumentDetailTarget<Result>(
  toTarget: (row: BatchJobItemRow<Result>) => DocumentDetailDialogTarget | null
): UseDocumentDetailTargetResult<Result> {
  const dialog = useDialogState<DocumentDetailDialogTarget>();
  const { open: openDialog } = dialog;

  const open = useCallback(
    (row: BatchJobItemRow<Result>) => {
      const target = toTarget(row);
      if (target) openDialog(target);
    },
    [toTarget, openDialog]
  );

  return { target: dialog.value, open, close: dialog.close };
}
